import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import Toggle from '~ComponentsImproved/Toggle';
import { useDispatch, useI18n, useLogger, useMetadata } from '~Hooks';
import { Metadata } from '~Hooks/useMetadata';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import { StationSettings } from './StationSettings';
import NumberSetting from '../../../Components/NumberSetting';
import * as actions from '~actions';

type ToggleConfig = {
  label: string;
  subLabel: string;
  key: string;
  storage: any;
  /** Will reverse the value of the storage */
  inverted?: boolean;
  /** Where the value is persisted. Defaults to device storage. */
  source?: 'storage' | 'metadata';
};

const KDSSettingsList: FC = () => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const meta = useMetadata();
  const logger = useLogger('settings-v2');
  const dispatch = useDispatch();

  const lateAfterMinutesInKDS = meta.data.lateAfterMinutesInKDS;

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (KDS Settings)', { fields: data });
    commitToStorage(data);
  }

  function updateMetadataSetting(data: Partial<Metadata>) {
    logger.info('Updating Settings (KDS Metadata)', { fields: data });
    meta.update(data);
  }

  /** Print-related settings, grouped under the "Prints" block. */
  const printsTogglesConfig: Array<ToggleConfig> = [
    {
      label: i18n('Settings.KDS.BongOnProgress'),
      subLabel: i18n('Settings.KDS.BongOnProgress.Text'),
      key: 'KDSBongOnProgress',
      storage: storage.KDSBongOnProgress,
    },
    {
      label: i18n('Settings.KDS.BongOnItemProgress'),
      subLabel: i18n('Settings.KDS.BongOnItemProgress.Text'),
      key: 'KDSBongOnItemProgress',
      storage: storage.KDSBongOnItemProgress,
    },
    {
      label: i18n('Settings.KDS.BongOnReady'),
      subLabel: i18n('Settings.KDS.BongOnReady.Text'),
      key: 'KDSBongOnReady',
      storage: storage.KDSBongOnReady,
    },
  ];

  const printOnStatusRadioConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: ORDER_STATUS | null;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.KDS.PhysicalPrint'),
      containerDesc: i18n('Settings.KDS.PhysicalPrint.Desc'),
      name: 'status',
      buttons: [
        {
          id: 'NONE',
          label: i18n('Settings.KDS.NoPrint'),
          value: null,
        },
        {
          id: ORDER_STATUS.ACCEPTED,
          label: i18n('Settings.KDS.Accepted'),
          value: ORDER_STATUS.ACCEPTED,
        },
        {
          id: ORDER_STATUS.FINALIZING,
          label: i18n('Settings.KDS.Finalizing'),
          value: ORDER_STATUS.FINALIZING,
        },
        {
          id: ORDER_STATUS.READY,
          label: i18n('Settings.KDS.Ready'),
          value: ORDER_STATUS.READY,
        },
      ],
    },
  ];

  /** Progress-related settings, grouped under the "Progress" block. */
  const progressTogglesConfig: Array<ToggleConfig> = [
    {
      label: i18n('Settings.KDS.TripleProgress'),
      subLabel: i18n('Settings.KDS.TripleProgress.Text'),
      key: 'tripleOrderItemProgress',
      storage: storage.tripleOrderItemProgress,
    },
    {
      label: i18n('Settings.KDS.DisableFullOrderProgress'),
      subLabel: i18n('Settings.KDS.DisableFullOrderProgress.Text'),
      key: 'disableOrderProgress',
      storage: storage.disableOrderProgress,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.DisableItemProgress'),
      subLabel: i18n('Settings.KDS.DisableItemProgress.Text'),
      key: 'disableItemProgress',
      storage: storage.disableItemProgress,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.ProgressOnlyFiltered'),
      subLabel: i18n('Settings.KDS.ProgressOnlyFiltered.Text'),
      key: 'KDSProgressOnlyFiltered',
      storage: storage.KDSProgressOnlyFiltered,
    },
  ];

  /** Remaining KDS settings, kept in their original order in the general block. */
  const generalTogglesConfig: Array<ToggleConfig> = [
    {
      label: i18n('Settings.KDS.OrderStatusFilter'),
      subLabel: i18n('Settings.KDS.OrderStatusFilter.Text'),
      key: 'KDSFilterOnWholeOrderStatus',
      storage: storage.KDSFilterOnWholeOrderStatus,
      inverted: true,
    },
    {
      label: i18n('Settings.KDS.ShowAllOrderStatuses'),
      subLabel: i18n('Settings.KDS.ShowAllOrderStatuses.Text'),
      key: 'KDSShowAllOrderStatuses',
      storage: storage.KDSShowAllOrderStatuses,
    },
    {
      label: i18n('Settings.KDS.PlaySound'),
      subLabel: i18n('Settings.KDS.PlaySound.Text'),
      key: 'KDSPlayNewOrderSound',
      storage: storage.KDSPlayNewOrderSound,
    },
  ];

  /** Course-related settings, grouped under the "Course types" block. */
  const courseTypesTogglesConfig: Array<ToggleConfig> = [
    {
      label: i18n('Settings.KDS.GroupItemsByCourseType'),
      subLabel: i18n('Settings.KDS.GroupItemsByCourseType.Text'),
      key: 'KDSGroupItemsByCourseType',
      storage: storage.KDSGroupItemsByCourseType,
    },
    {
      label: i18n('Settings.KDS.CourseOrdersMode'),
      subLabel: i18n('Settings.KDS.CourseOrdersMode.Text'),
      key: 'useCoursesInKDS',
      storage: meta.data.useCoursesInKDS,
      source: 'metadata',
    },
    {
      label: i18n('Settings.KDS.HoldFirstCourse'),
      subLabel: i18n('Settings.KDS.HoldFirstCourse.Text'),
      key: 'holdFirstCourse',
      storage: storage.holdFirstCourse,
    },
  ];

  if (storageLoading) {
    return null;
  }

  const updateOrderStatus = (status: ORDER_STATUS | null) => {
    logger.info('Updating Settings (Bong Settings) - Order status', { fields: { status } });
    updateStorage({ KDSBongOnOrderStatus: status });
  };

  /**
   * Validates the submitted threshold before it reaches venue metadata. Returns
   * false on an invalid value so the input stays open with what was typed.
   */
  const updateLateAfterMinutesInKDS = (value: number | '') => {
    const isInvalidValue = value === '' || !Number.isInteger(value) || value < 1;

    if (isInvalidValue) {
      logger.warn('Invalid late order threshold submitted', { value });
      dispatch(
        actions.addToast(i18n('Settings.KDS.DueLaterThreshold.InvalidValue'), true, null, false),
      );
      return false;
    }

    if (value === lateAfterMinutesInKDS) {
      dispatch(
        actions.addToast(i18n('Settings.KDS.DueLaterThreshold.AlreadyUpdated'), true, null, false),
      );
      return true;
    }

    updateMetadataSetting({
      lateAfterMinutesInKDS: value,
    });

    dispatch(actions.addToast(i18n('Settings.KDS.DueLaterThreshold.Updated'), true, null, false));

    return true;
  };

  /** Renders a toggle row from a toggle config entry. */
  const renderToggle = (toggle: ToggleConfig) => (
    <Toggle
      key={toggle.key}
      settingKey={toggle.key}
      label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
      description={toggle.subLabel}
      initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
      onToggle={() =>
        toggle.source === 'metadata'
          ? updateMetadataSetting({ [toggle.key]: !toggle.storage } as Partial<Metadata>)
          : updateStorage({ [toggle.key]: !toggle.storage })
      }
      fullWidth
      darkBackground
      lightText
    />
  );

  return (
    <Box display="flex" flexDirection="column" gap="large">
      <SettingsBlock>
        <StationSettings />

        {generalTogglesConfig.map(renderToggle)}

        <NumberSetting
          label={i18n('Settings.KDS.DueLaterThreshold')}
          description={i18n('Settings.KDS.DueLaterThreshold.Text')}
          value={lateAfterMinutesInKDS}
          displayValue={i18n('KDS.PrepTime.Minutes', lateAfterMinutesInKDS)}
          settingKey="lateAfterMinutesInKDS"
          darkBackground
          fullWidth
          onSave={updateLateAfterMinutesInKDS}
        />
      </SettingsBlock>

      <SettingsBlock title={i18n('Settings.KDS.Prints')}>
        {printsTogglesConfig.map(renderToggle)}

        {printOnStatusRadioConfig.map((config) => (
          <RadioButtonSelect
            key={config.name}
            settingKey="KDSBongOnOrderStatus"
            data={config}
            selected={storage.KDSBongOnOrderStatus ?? 'NONE'}
            handleChange={(value) => updateOrderStatus(value)}
          />
        ))}
      </SettingsBlock>

      <SettingsBlock title={i18n('Settings.KDS.Progress')}>
        {progressTogglesConfig.map(renderToggle)}
      </SettingsBlock>

      <SettingsBlock title={i18n('Settings.KDS.CourseTypes')}>
        {courseTypesTogglesConfig.map(renderToggle)}
      </SettingsBlock>
    </Box>
  );
};

export default KDSSettingsList;
